import React, { createContext, useState, ReactNode, useEffect, useContext } from 'react';
import { supabase, ensureRealtimeAuth } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { Kegiatan, NewKegiatanData, KegiatanAbsen, KegiatanAbsenStatus, KegiatanSesi } from '../types';
import { useGuru } from './GuruContext';
import { useUserAccess } from './UserAccessContext';
import { useMasterData } from './MasterDataContext';
import { getOfficerName } from '../utils/officer';
import { fetchAllRows } from '../utils/supabasePagination';
import { PETUGAS_SISTEM, toWibDateStr } from '../utils/kegiatan';
import {
    mapKegiatanFromDb,
    mapKegiatanToDb,
    mapKegiatanAbsenFromDb,
    mapKegiatanAbsenToDb
} from '../utils/kegiatanMapper';

export interface KegiatanSantriContextType {
    loading: boolean;
    kegiatanList: Kegiatan[];
    kegiatanAbsenRecords: KegiatanAbsen[];

    addKegiatan: (data: NewKegiatanData) => Promise<any>;
    updateKegiatan: (id: string, data: Partial<NewKegiatanData>) => Promise<void>;
    deleteKegiatan: (id: string) => Promise<void>;
    fetchKegiatanAbsen: (kegiatanId: string) => Promise<KegiatanAbsen[]>;
    fetchKegiatanAbsenByDate: (startDate: Date, endDate: Date) => Promise<KegiatanAbsen[]>;
    fetchKegiatanAbsenBySantriIds: (santriIds: string[], startDate?: Date, endDate?: Date) => Promise<KegiatanAbsen[]>;
    addKegiatanAbsenBatch: (absenBatch: Omit<KegiatanAbsen, 'id' | 'petugas'>[], opsi?: { petugas?: string }) => Promise<void>;
    deleteKegiatanAbsenRecords: (recordIds: string[]) => Promise<void>;
    updateKegiatanAbsenRecord: (recordId: string, newStatus: KegiatanAbsenStatus) => Promise<void>;
    fetchKegiatanSesi: (kegiatanId: string, tanggalWib: string) => Promise<KegiatanSesi[]>;
    tutupPresensi: (input: TutupPresensiInput) => Promise<TutupPresensiHasil>;
    batalkanTutupPresensi: (hasil: TutupPresensiHasil) => Promise<void>;
}

export interface TutupPresensiInput {
    kegiatan: Kegiatan;
    /** Santri yang belum punya data hari ini, dengan status yang akan dicatat. */
    entries: { studentId: string; studentName: string; kelas: string; status: 'Alpha' | 'Izin' }[];
    /** Kelas/asrama yang ditutup (dicatat ke kegiatan_sesi). */
    units: { unitTipe: 'kelas' | 'asrama'; unit: string; jumlahAlpha: number; jumlahIzin: number }[];
}

export interface TutupPresensiHasil {
    kegiatanId: string;
    absenIds: string[];
    sesiIds: string[];
}

const mapKegiatanSesiFromDb = (row: any): KegiatanSesi => ({
    id: row.id,
    kegiatanId: row.kegiatan_id,
    tanggalWib: row.tanggal_wib,
    unitTipe: row.unit_tipe,
    unit: row.unit,
    status: row.status,
    jumlahAlpha: row.jumlah_alpha ?? 0,
    jumlahIzin: row.jumlah_izin ?? 0,
    ditutupOleh: row.ditutup_oleh ?? '',
    ditutupPada: new Date(row.ditutup_pada),
});

export const KegiatanSantriContext = createContext<KegiatanSantriContextType | undefined>(undefined);

export const useKegiatanSantri = () => {
    const context = useContext(KegiatanSantriContext);
    if (context === undefined) {
        throw new Error('useKegiatanSantri must be used within a KegiatanSantriProvider');
    }
    return context;
};

export const KegiatanSantriProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { authUser } = useAuth();
    const { guruRecords } = useGuru();
    const { isSuperAdmin, userPermissions } = useUserAccess();
    const { tahunAjaranRecords, activeSemester, fetchTahunAjaranData } = useMasterData();

    useEffect(() => {
        fetchTahunAjaranData();
    }, [fetchTahunAjaranData]);

    const activeTahunAjaran = React.useMemo(() => {
        const activeTA = tahunAjaranRecords.find(t => t.status === 'Aktif');
        if (activeTA?.nama) return activeTA.nama;
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        return currentMonth >= 7
            ? `${currentYear}/${currentYear + 1}`
            : `${currentYear - 1}/${currentYear}`;
    }, [tahunAjaranRecords]);

    const [kegiatanList, setKegiatanList] = useState<Kegiatan[]>([]);
    const [kegiatanAbsenRecords, setKegiatanAbsenRecords] = useState<KegiatanAbsen[]>([]);
    const [loading, setLoading] = useState(true);

    // Determines if the current user needs to globally monitor activities (Admins, Kesantrian, etc)
    const hasKegiatanAccess = React.useMemo(() => {
        if (!authUser) return false;
        if (isSuperAdmin) return true;
        const permData = userPermissions?.find(p => p.email === authUser.email);
        const allowedMenus = permData?.allowedMenus || [];
        // Typically, those involved in checking activity logs globally
        return allowedMenus.some(menu => ['pg-kepala-kesantrian', 'pg-murobbi', 'kegiatan-santri'].includes(menu));
    }, [authUser, isSuperAdmin, userPermissions]);

    useEffect(() => {
        let isMounted = true;
        if (!authUser) {
            setKegiatanList([]);
            setKegiatanAbsenRecords([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        // Fetch kegiatan list (one-time; small dataset used for dropdowns)
        const fetchKegiatan = async () => {
            try {
                const { data, error } = await supabase
                    .from('kegiatan')
                    .select('*')
                    .order('nama_kegiatan', { ascending: true });
                if (error) { console.error(error); return; }
                if (isMounted) setKegiatanList((data || []).map(mapKegiatanFromDb));
            } catch (e) { console.error(e); }
        };

        fetchKegiatan();

        // Setup realtime listener for 'kegiatan' table changes
        let kegiatanChannel: ReturnType<typeof supabase.channel> | null = null;
        const setupKegiatanRealtime = async () => {
            try {
                await ensureRealtimeAuth();
                kegiatanChannel = supabase
                    .channel('kegiatan_table_changes')
                    .on(
                        'postgres_changes',
                        { event: '*', schema: 'public', table: 'kegiatan' },
                        () => {
                            fetchKegiatan();
                        }
                    )
                    .subscribe();
            } catch (e) {
                console.warn('[Kegiatan Realtime] Gagal subscribe kegiatan:', e);
            }
        };
        setupKegiatanRealtime();

        let cleanupRealtime = () => {};

        // Hybrid Provider: only attach 48-hour global listener for Admins
        if (hasKegiatanAccess) {
            const fortyEightHoursAgoISO = () => new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();

            const loadAbsenRecords = async () => {
                // Paginasi: PostgREST memotong hasil di 1000 baris tanpa error.
                // Record 48 jam semua kegiatan (apalagi Parenting = 2 record/santri)
                // mudah melewati 1000, sehingga progress di Tab Kegiatan/Ringkasan jadi kurang.
                const startISO = fortyEightHoursAgoISO();
                let data: any[];
                try {
                    data = await fetchAllRows<any>((from, to) => supabase
                        .from('kegiatan_absen')
                        .select('*')
                        .gte('tanggal', startISO)
                        .order('tanggal', { ascending: false })
                        .order('id', { ascending: true })
                        .range(from, to));
                } catch (error) { console.error(error); return; }
                if (isMounted) {
                    setKegiatanAbsenRecords(data.map(mapKegiatanAbsenFromDb));
                    setLoading(false);
                }
            };

            loadAbsenRecords();

            let channel: ReturnType<typeof supabase.channel> | null = null;
            let debounceTimer: ReturnType<typeof setTimeout> | null = null;

            const scheduleReload = () => {
                if (debounceTimer) clearTimeout(debounceTimer);
                // debounce: bulk insert/update menghasilkan banyak event realtime,
                // gabungkan jadi satu re-fetch.
                debounceTimer = setTimeout(() => loadAbsenRecords(), 500);
            };

            const setupRealtime = async () => {
                try {
                    await ensureRealtimeAuth();
                    channel = supabase
                        .channel('kegiatan_absen_changes')
                        .on(
                            'postgres_changes',
                            { event: '*', schema: 'public', table: 'kegiatan_absen' },
                            () => scheduleReload()
                        )
                        .subscribe();
                } catch (e) {
                    console.warn('[Kegiatan Realtime] Gagal subscribe:', e);
                }
            };

            setupRealtime();

            cleanupRealtime = () => {
                if (debounceTimer) clearTimeout(debounceTimer);
                if (channel) supabase.removeChannel(channel);
            };
        } else {
            // Idle mode for regular users / parents
            if (isMounted) {
                setKegiatanAbsenRecords([]);
                setLoading(false);
            }
        }

        return () => {
            isMounted = false;
            if (kegiatanChannel) supabase.removeChannel(kegiatanChannel);
            cleanupRealtime();
        };
    }, [authUser, hasKegiatanAccess]);

    const addKegiatan = async (data: NewKegiatanData): Promise<any> => {
        const payload = mapKegiatanToDb(data);
        const { data: inserted, error } = await supabase
            .from('kegiatan')
            .insert([payload])
            .select();
        if (error) { console.error(error); throw error; }
        const newRecord = inserted?.[0] ? mapKegiatanFromDb(inserted[0]) : null;
        if (newRecord) {
            setKegiatanList(prev => [...prev, newRecord].sort((a, b) => a.namaKegiatan.localeCompare(b.namaKegiatan)));
        }
        return inserted?.[0];
    };

    const updateKegiatan = async (id: string, data: Partial<NewKegiatanData>) => {
        const payload = mapKegiatanToDb(data);
        // Optimistic UI update
        setKegiatanList(prev =>
            prev.map(item => (item.id === id ? { ...item, ...data } : item))
        );
        const { error } = await supabase
            .from('kegiatan')
            .update(payload)
            .eq('id', id);
        if (error) { console.error(error); throw error; }
    };

    const deleteKegiatan = async (id: string) => {
        // Optimistic UI update
        setKegiatanList(prev => prev.filter(item => item.id !== id));
        const { error } = await supabase
            .from('kegiatan')
            .delete()
            .eq('id', id);
        if (error) { console.error(error); throw error; }
    };

    const fetchKegiatanAbsen = React.useCallback(async (kegiatanId: string): Promise<KegiatanAbsen[]> => {
        // Dipakai layar presensi yang hanya butuh data hari ini. Sejak data disimpan
        // per hari, kegiatan rutin menumpuk satu baris per santri per hari, jadi
        // cukup ambil sejak kemarin (bukan seluruh riwayat).
        const sejakKemarin = new Date();
        sejakKemarin.setDate(sejakKemarin.getDate() - 1);
        sejakKemarin.setHours(0, 0, 0, 0);
        let data: any[];
        try {
            data = await fetchAllRows<any>((from, to) => supabase
                .from('kegiatan_absen')
                .select('*')
                .eq('kegiatan_id', kegiatanId)
                .gte('tanggal', sejakKemarin.toISOString())
                .order('id', { ascending: true })
                .range(from, to));
        } catch (error) { console.error(error); return []; }
        const records = data.map(mapKegiatanAbsenFromDb);
        setKegiatanAbsenRecords(prev => {
            // Merge or replace records for this specific kegiatanId so state updates reliably for non-superadmins
            const filteredOther = prev.filter(r => r.kegiatanId !== kegiatanId);
            return [...filteredOther, ...records];
        });
        return records;
    }, []);

    const fetchKegiatanAbsenBySantriIds = async (santriIds: string[], startDate?: Date, endDate?: Date): Promise<KegiatanAbsen[]> => {
        if (!santriIds || santriIds.length === 0) return [];

        let q = supabase
            .from('kegiatan_absen')
            .select('*')
            .in('student_id', santriIds);

        if (startDate && endDate) {
            const start = new Date(startDate); start.setHours(0, 0, 0, 0);
            const end = new Date(endDate); end.setHours(23, 59, 59, 999);
            q = q.gte('tanggal', start.toISOString()).lte('tanggal', end.toISOString());
        }

        const { data, error } = await q.order('tanggal', { ascending: false });
        if (error) { console.error(error); return []; }
        return (data || []).map(mapKegiatanAbsenFromDb);
    };

    const fetchKegiatanAbsenByDate = async (startDate: Date, endDate: Date): Promise<KegiatanAbsen[]> => {
        const start = new Date(startDate); start.setHours(0, 0, 0, 0);
        const end = new Date(endDate); end.setHours(23, 59, 59, 999);

        try {
            const data = await fetchAllRows<any>((from, to) => supabase
                .from('kegiatan_absen')
                .select('*')
                .gte('tanggal', start.toISOString())
                .lte('tanggal', end.toISOString())
                .order('tanggal', { ascending: false })
                .order('id', { ascending: true })
                .range(from, to));
            return data.map(mapKegiatanAbsenFromDb);
        } catch (error) { console.error(error); return []; }
    };

    const addKegiatanAbsenBatch = async (absenBatch: Omit<KegiatanAbsen, 'id' | 'petugas'>[], opsi?: { petugas?: string }) => {
        if (!absenBatch || absenBatch.length === 0) return;
        const kegiatanId = absenBatch[0]?.kegiatanId;
        if (!kegiatanId) return;

        // Kiosk mencatat petugas sebagai "Kiosk <tempat>", bukan nama akun.
        const petugasName = opsi?.petugas || getOfficerName(guruRecords, authUser);
        const currentOfficer = guruRecords.find(p => p.nama === petugasName);
        const officerGender = currentOfficer?.jenisKelamin;

        const isParentingActivity = absenBatch[0]?.isParenting;

        let parentingTamu: 'Ikhwan' | 'Akhwat' | undefined;
        let parentingLabel: 'Ayah' | 'Ibu' | undefined;

        if (isParentingActivity && officerGender) {
            if (officerGender === 'Laki-laki') {
                parentingTamu = 'Ikhwan';
                parentingLabel = 'Ayah';
            } else if (officerGender === 'Perempuan') {
                parentingTamu = 'Akhwat';
                parentingLabel = 'Ibu';
            }
        }

        // Dedup eksplisit: ambil record existing utk kegiatan ini PADA HARI YANG SAMA,
        // lalu pisahkan mana yang update (by id) vs insert (id auto).
        // Kunci dedup: non-parenting → student_id; parenting → student_id + label.
        // Tanpa filter hari, presensi kegiatan rutin menimpa data hari sebelumnya.
        const tanggalWib = toWibDateStr(absenBatch[0]?.tanggal ? new Date(absenBatch[0].tanggal) : new Date());
        const { data: existingRows, error: qErr } = await supabase
            .from('kegiatan_absen')
            .select('id, student_id, label')
            .eq('kegiatan_id', kegiatanId)
            .eq('tanggal_wib', tanggalWib);
        if (qErr) { console.error(qErr); throw qErr; }
        const existing = existingRows || [];

        const updates: { id: string; payload: Record<string, any> }[] = [];
        const inserts: Record<string, any>[] = [];

        absenBatch.forEach(absen => {
            const absenDataToMap: Partial<KegiatanAbsen> = {
                tahunAjaran: activeTahunAjaran,
                semester: activeSemester,
                ...absen,
                petugas: petugasName
            };
            const payload = mapKegiatanAbsenToDb(absenDataToMap);

            if (isParentingActivity && officerGender) {
                payload.tamu = parentingTamu;
                payload.label = parentingLabel;

                const existingRecord = existing.find(d =>
                    d.student_id === absen.studentId && d.label === parentingLabel
                );

                if (existingRecord) {
                    updates.push({ id: existingRecord.id, payload });
                } else {
                    inserts.push(payload);
                }
            } else {
                const existingRecord = existing.find(d => d.student_id === absen.studentId);
                if (existingRecord) {
                    updates.push({ id: existingRecord.id, payload });
                } else {
                    inserts.push(payload);
                }
            }
        });

        let gagal: unknown = null;
        for (const u of updates) {
            const { error } = await supabase.from('kegiatan_absen').update(u.payload).eq('id', u.id);
            if (error) { console.error('Gagal update absen:', error); gagal = gagal || error; }
        }

        if (inserts.length > 0) {
            const { error } = await supabase.from('kegiatan_absen').insert(inserts);
            if (error) { console.error('Gagal insert absen:', error); gagal = gagal || error; }
        }

        // Re-fetch attendance records for this activity to ensure local state (kegiatanAbsenRecords)
        // is immediately updated for all users/officers regardless of superadmin status
        await fetchKegiatanAbsen(kegiatanId);

        // Dilempar supaya layar tidak menampilkan "berhasil" untuk data yang gagal tersimpan.
        if (gagal) throw gagal;
    };

    const deleteKegiatanAbsenRecords = async (recordIds: string[]) => {
        if (recordIds.length === 0) return;
        const { error } = await supabase
            .from('kegiatan_absen')
            .delete()
            .in('id', recordIds);
        if (error) { console.error(error); throw error; }
    };

    const updateKegiatanAbsenRecord = async (recordId: string, newStatus: KegiatanAbsenStatus) => {
        const { error } = await supabase
            .from('kegiatan_absen')
            .update({ status: newStatus })
            .eq('id', recordId);
        if (error) { console.error(error); throw error; }
    };

    const fetchKegiatanSesi = React.useCallback(async (kegiatanId: string, tanggalWib: string): Promise<KegiatanSesi[]> => {
        const { data, error } = await supabase
            .from('kegiatan_sesi')
            .select('*')
            .eq('kegiatan_id', kegiatanId)
            .eq('tanggal_wib', tanggalWib);
        if (error) { console.error(error); return []; }
        return (data || []).map(mapKegiatanSesiFromDb);
    }, []);

    const tutupPresensi = async ({ kegiatan, entries, units }: TutupPresensiInput): Promise<TutupPresensiHasil> => {
        const now = new Date();
        const tanggalWib = toWibDateStr(now);
        const petugasName = getOfficerName(guruRecords, authUser);
        const hasil: TutupPresensiHasil = { kegiatanId: kegiatan.id, absenIds: [], sesiIds: [] };

        const rows = entries.map(e => mapKegiatanAbsenToDb({
            kegiatanId: kegiatan.id,
            namaKegiatan: kegiatan.namaKegiatan,
            studentId: e.studentId,
            studentName: e.studentName,
            kelas: e.kelas,
            status: e.status,
            tanggal: now,
            // Petugas 'Sistem' menandai record hasil penutupan, sehingga santri
            // yang datang telat masih bisa scan (Alpha diganti Hadir).
            petugas: PETUGAS_SISTEM,
            tahunAjaran: activeTahunAjaran,
            semester: activeSemester,
            isParenting: !!kegiatan.isParenting,
        }));

        if (rows.length > 0) {
            const { data, error } = await supabase.from('kegiatan_absen').insert(rows).select('id');
            if (!error) {
                hasil.absenIds = (data || []).map((d: any) => d.id);
            } else if (error.code === '23505') {
                // Ada santri yang baru saja presensi dari perangkat lain: simpan satu per satu,
                // lewati yang bentrok.
                for (const row of rows) {
                    const { data: one, error: e1 } = await supabase.from('kegiatan_absen').insert(row).select('id');
                    if (!e1 && one?.[0]) hasil.absenIds.push(one[0].id);
                    else if (e1 && e1.code !== '23505') console.error('Gagal tutup presensi:', e1);
                }
            } else {
                console.error(error);
                throw error;
            }
        }

        if (units.length > 0) {
            const { data, error } = await supabase.from('kegiatan_sesi').upsert(
                units.map(u => ({
                    kegiatan_id: kegiatan.id,
                    tanggal_wib: tanggalWib,
                    unit_tipe: u.unitTipe,
                    unit: u.unit,
                    status: 'ditutup',
                    jumlah_alpha: u.jumlahAlpha,
                    jumlah_izin: u.jumlahIzin,
                    ditutup_oleh: petugasName,
                    ditutup_pada: now.toISOString(),
                })),
                { onConflict: 'kegiatan_id,tanggal_wib,unit_tipe,unit' }
            ).select('id');
            if (error) console.error('Gagal mencatat sesi presensi:', error);
            else hasil.sesiIds = (data || []).map((d: any) => d.id);
        }

        await fetchKegiatanAbsen(kegiatan.id);
        return hasil;
    };

    const batalkanTutupPresensi = async (hasil: TutupPresensiHasil) => {
        if (hasil.absenIds.length > 0) {
            const { error } = await supabase.from('kegiatan_absen').delete().in('id', hasil.absenIds);
            if (error) { console.error(error); throw error; }
        }
        if (hasil.sesiIds.length > 0) {
            const { error } = await supabase.from('kegiatan_sesi').delete().in('id', hasil.sesiIds);
            if (error) console.error(error);
        }
        await fetchKegiatanAbsen(hasil.kegiatanId);
    };

    return (
        <KegiatanSantriContext.Provider value={{
            loading,
            kegiatanList, kegiatanAbsenRecords,
            addKegiatan, updateKegiatan, deleteKegiatan,
            fetchKegiatanAbsen, fetchKegiatanAbsenByDate, fetchKegiatanAbsenBySantriIds, addKegiatanAbsenBatch,
            deleteKegiatanAbsenRecords, updateKegiatanAbsenRecord,
            fetchKegiatanSesi, tutupPresensi, batalkanTutupPresensi
        }}>
            {children}
        </KegiatanSantriContext.Provider>
    );
};
